import React, { useState } from "react";
import { YStack, Text, Button } from "tamagui";
import { useApiPost, useApiQuery, useApiQueryClient } from "@/lib/api";

import { CommentCard } from "./components/CommentCard";
import { ReplyIndicator } from "./components/ReplyIndicator";
import { brandShadowSm } from "../../../../../../tamagui.config";
import { CommentWithReplies } from "@/types/comments";
import { TextArea } from "@/components/design-system";

type EventDetailCommentsProps = {
  eventId: string;
  onCommentSubmitting?: (isSubmitting: boolean) => void;
};

export const EventDetailComments = ({
  eventId,
  onCommentSubmitting,
}: EventDetailCommentsProps) => {
  const queryClient = useApiQueryClient();

  const { data: comments } = useApiQuery<{
    comments: CommentWithReplies[];
  }>(["comments", eventId], `/api/v1/events/${eventId}/comments`, {
    refetchOnMount: "always",
  });

  const [inputValue, setInputValue] = useState("");
  const [replyingTo, setReplyingTo] = useState<CommentWithReplies | null>(null);

  const { mutate: addComment, isPending: isAddingComment } = useApiPost(
    `/api/v1/events/${eventId}/comments`
  );

  const handleSubmit = () => {
    if (!inputValue.trim()) return;

    const payload = replyingTo
      ? { content: inputValue, parent_comment_id: replyingTo.id }
      : { content: inputValue };

    onCommentSubmitting?.(true);
    addComment(payload, {
      onSuccess: (response) => {
        console.log(response);
        setInputValue("");
        setReplyingTo(null);
        // Invalidate and refetch the comments query
        queryClient.invalidateQueries({ queryKey: ["comments", eventId] });
        onCommentSubmitting?.(false);
      },
      onError: () => {
        onCommentSubmitting?.(false);
      },
    });
  };

  const handleReply = (comment: CommentWithReplies) => {
    setReplyingTo(comment);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const placeholder = replyingTo ? "Write a reply..." : "Add a comment...";
  const buttonText = replyingTo ? "REPLY" : "ADD COMMENT";

  return (
    <YStack gap="$2" mb="$4" px="$4">
      <Text fontFamily="$heading" fontSize={16} textTransform="uppercase">
        Comments
      </Text>

      {comments?.comments?.map((commentItem) => (
        <CommentCard
          key={commentItem.id}
          comment={commentItem}
          onReply={handleReply}
        />
      ))}

      <YStack gap="$2" mt="$2">
        {replyingTo ? (
          <ReplyIndicator
            replyingToContent={replyingTo.content}
            onCancel={handleCancelReply}
          />
        ) : null}
        <TextArea
          placeholder={placeholder}
          numberOfLines={3}
          value={inputValue}
          onChangeText={setInputValue}
        />
      </YStack>
      <Button
        onPress={handleSubmit}
        disabled={isAddingComment || !inputValue.trim()}
        bg="$brandYellow"
        pressStyle={{
          bg: "$brandYellow",
          shadowColor: "$colorTransparent",
          shadowOffset: { width: 0, height: 0 },
          borderWidth: 1,
          borderColor: "black",
          scale: 0.98,
        }}
        borderWidth={1}
        fontSize={16}
        fontFamily="$heading"
        borderColor={"black"}
        {...brandShadowSm}
      >
        {buttonText}
      </Button>
    </YStack>
  );
};
